import React from "react";

import { Slot, type Slottable } from "~/components/slot";
import { type VariantProps, cv } from "~/tools/class-variance";

import styles from "./_form.module.css";
import { useFormFieldContext } from "./use-form-field-context";

const variants = cv(styles.formMessage, {
	variants: {
		variant: {
			error: styles.error,
		},
		unstyled: {
			true: styles.unstyled,
			false: null,
		},
	},
	defaultVariants: {
		unstyled: false,
	},
});

export interface FormMessageProps
	extends Slottable,
		VariantProps<typeof variants>,
		React.HTMLAttributes<HTMLParagraphElement> {
	unstyled?: boolean;
}

/**
 * A message that is displayed below a form field.
 * It can be used to provide additional information or error messages.
 */
export const FormMessage = React.forwardRef<HTMLParagraphElement, FormMessageProps>(
	({ asChild, className, variant, unstyled, ...props }, ref) => {
		const Root = asChild ? Slot : "p";

		const counter = useFormFieldContext()?.errorMessageDescendants;

		React.useLayoutEffect(() => {
			if (variant === "error" && counter != null) {
				counter.setValue((count) => count + 1);

				return () => {
					counter.setValue((count) => count - 1);
				};
			}
		}, [counter, variant]);

		return <Root className={variants({ variant, unstyled, className })} ref={ref} {...props} />;
	},
);

if (import.meta.env.DEV) {
	FormMessage.displayName = "FormMessage";
}
